import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Building2, Users, Settings, ChevronRight, Check, Loader2, Upload, ArrowLeft } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const MODULES_LIST = [
  { id: "crm", label: "CRM & Clients", desc: "Gestion des clients et contacts" },
  { id: "projects", label: "Projets", desc: "Suivi des projets et taches" },
  { id: "proposals", label: "Propositions", desc: "Devis et propositions modulaires" },
  { id: "invoices", label: "Facturation", desc: "Factures et paiements" },
  { id: "tickets", label: "Tickets", desc: "Support client" },
  { id: "seo", label: "SEO & Analytics", desc: "Audit et suivi SEO" },
  { id: "social", label: "Social Media", desc: "Gestion des reseaux sociaux" },
  { id: "contracts", label: "Contrats", desc: "Generation et signature" },
];

const STEPS = [
  { id: 1, label: "Votre agence", icon: Building2 },
  { id: 2, label: "Modules", icon: Settings },
  { id: 3, label: "Premier client", icon: Users },
  { id: 4, label: "Terminer", icon: Check },
];

export default function OnboardingPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agency, setAgency] = useState(null);

  // Step 1: Agency info
  const [agencyName, setAgencyName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");

  // Step 2: Modules
  const [selectedModules, setSelectedModules] = useState(["crm", "projects", "proposals", "invoices"]);

  // Step 3: First client
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => {
    const fetchAgency = async () => {
      try {
        const res = await fetch(`${API}/api/saas/agency`, { headers });
        const data = await res.json();
        setAgency(data);
        if (data.onboarding_completed) {
          navigate("/app/dashboard");
          return;
        }
        setAgencyName(data.name || "");
        setPhone(data.phone || "");
        setAddress(data.address || "");
        setCity(data.city || "");
        if (data.modules?.length) setSelectedModules(data.modules);
        if (data.onboarding_step > 1) setStep(data.onboarding_step);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAgency();
  }, [token]);

  const saveStep = async (nextStep) => {
    setSaving(true);
    try {
      if (step === 1) {
        await fetch(`${API}/api/saas/agency`, {
          method: "PUT",
          headers,
          body: JSON.stringify({ name: agencyName, phone, address, city, onboarding_step: nextStep }),
        });
      } else if (step === 2) {
        await fetch(`${API}/api/saas/agency`, {
          method: "PUT",
          headers,
          body: JSON.stringify({ modules: selectedModules, onboarding_step: nextStep }),
        });
      } else if (step === 3 && clientName && clientEmail) {
        await fetch(`${API}/api/clients`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            company_name: clientName,
            contact_email: clientEmail,
            phone: clientPhone,
            sector: "general",
          }),
        });
      }
      setStep(nextStep);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/saas/agency/complete-onboarding`, { method: "POST", headers });
      navigate("/app/dashboard");
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (id) => {
    setSelectedModules((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#080C18]"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;

  return (
    <div className="min-h-screen bg-[#080C18] flex flex-col items-center justify-center p-4" data-testid="onboarding-page">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8 px-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <div key={s.id} className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                  isDone ? "bg-emerald-500 border-emerald-500" : isActive ? "border-cyan-500 bg-cyan-500/20" : "border-slate-600 bg-slate-800"
                }`}>
                  {isDone ? <Check className="w-5 h-5 text-white" /> : <Icon className={`w-5 h-5 ${isActive ? "text-cyan-400" : "text-slate-500"}`} />}
                </div>
                <span className={`text-sm hidden sm:inline ${isActive ? "text-white font-medium" : isDone ? "text-emerald-400" : "text-slate-500"}`}>
                  {s.label}
                </span>
                {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-600 mx-2" />}
              </div>
            );
          })}
        </div>

        {/* Card */}
        <div className="bg-[#0F1629] border border-slate-700/50 rounded-2xl p-8">
          {/* Step 1: Agency Info */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Configurez votre agence</h2>
                <p className="text-slate-400 mt-1">Informations de base qui apparaitront sur vos documents.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Nom de l'agence *</label>
                  <input data-testid="onboarding-agency-name" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="Mon Agence" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300 block mb-1.5">Telephone</label>
                    <input data-testid="onboarding-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="06 XX XX XX XX" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 block mb-1.5">Ville</label>
                    <input data-testid="onboarding-city" value={city} onChange={(e) => setCity(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="Paris" />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Adresse</label>
                  <input data-testid="onboarding-address" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="123 Rue de..." />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Modules */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Choisissez vos modules</h2>
                <p className="text-slate-400 mt-1">Selectionnez les fonctionnalites dont vous avez besoin.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {MODULES_LIST.map((mod) => {
                  const isSelected = selectedModules.includes(mod.id);
                  return (
                    <button
                      key={mod.id}
                      data-testid={`module-${mod.id}`}
                      onClick={() => toggleModule(mod.id)}
                      className={`text-left p-4 rounded-xl border transition-all ${
                        isSelected ? "border-cyan-500/50 bg-cyan-500/10" : "border-slate-700 bg-[#0B0F19] hover:border-slate-600"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white">{mod.label}</span>
                        {isSelected && <Check className="w-4 h-4 text-cyan-400" />}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{mod.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: First Client */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white">Ajoutez votre premier client</h2>
                <p className="text-slate-400 mt-1">Optionnel - vous pourrez en ajouter plus tard.</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Nom de l'entreprise</label>
                  <input data-testid="onboarding-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="Nom du client" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300 block mb-1.5">Email</label>
                    <input data-testid="onboarding-client-email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="email@client.com" type="email" />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 block mb-1.5">Telephone</label>
                    <input data-testid="onboarding-client-phone" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 outline-none" placeholder="06 XX XX XX XX" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 4 && (
            <div className="text-center space-y-6 py-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center mx-auto">
                <Check className="w-10 h-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Tout est pret !</h2>
                <p className="text-slate-400 mt-2">Votre agence <span className="text-cyan-400 font-medium">{agencyName}</span> est configuree.</p>
                <p className="text-slate-500 text-sm mt-1">{selectedModules.length} modules actifs</p>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700/50">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> Retour
              </button>
            ) : <div />}
            {step < 4 ? (
              <button
                data-testid="onboarding-next"
                onClick={() => saveStep(step + 1)}
                disabled={saving || (step === 1 && !agencyName)}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Suivant <ChevronRight className="w-4 h-4" /></>}
              </button>
            ) : (
              <button
                data-testid="onboarding-complete"
                onClick={completeOnboarding}
                disabled={saving}
                className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-8 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Acceder au tableau de bord <ChevronRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
